/**
 * Was this page load reached via a GoTrue invite/recovery/magic-link
 * redirect? `/invite/accept` used to trust ANY live session enough to let
 * it set a new password with no re-authentication (fix round 1, item 3) —
 * a stolen or shared browser tab with an ordinary signed-in session could
 * silently take over the account by just visiting the URL.
 *
 * GoTrue encodes the link type in the redirect URL itself — as a
 * `type=invite|recovery|magiclink|signup` param, in the hash fragment
 * (implicit flow) or the query string (PKCE, alongside `code=`). The
 * Supabase client's `detectSessionInUrl: true` consumes and strips that
 * fragment/param automatically and asynchronously on load, so by the time
 * a React component mounts and could inspect `window.location`, it may
 * already be gone. `parseAuthLinkType` is pure (Vitest-testable without a
 * DOM); `captureAuthLinkType()` snapshots the real URL and must be called
 * at module-eval time — see `supabaseClient.business.ts`, which imports
 * this module (for its side effect) before constructing the client, so the
 * capture happens before GoTrue's async URL-detection has a chance to run
 * (module top-level code across the whole import graph all runs
 * synchronously, before any promise the client's own initialisation
 * schedules can resolve).
 */
export type AuthLinkType = 'invite' | 'recovery' | 'signup' | 'magiclink' | 'code' | null;

export function parseAuthLinkType(hash: string, search: string): AuthLinkType {
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(search.replace(/^\?/, ''));
  const type = hashParams.get('type') ?? searchParams.get('type');
  if (type === 'invite' || type === 'recovery' || type === 'signup' || type === 'magiclink') {
    return type;
  }
  // PKCE flow: a bare `?code=` with no `type` param still means this load
  // came from an auth link (GoTrue's `exchangeCodeForSession` path) — the
  // Supabase client consumes it the same way.
  if (searchParams.has('code')) return 'code';
  return null;
}

let capturedAuthLinkType: AuthLinkType = null;
let captured = false;

/** Snapshots the current `window.location` — idempotent (only the first
 * call in a page's lifetime has an uncorrupted URL to read). Call once, as
 * early as possible (see module doc above). */
export function captureAuthLinkType(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  capturedAuthLinkType = parseAuthLinkType(window.location.hash, window.location.search);
}

export function getCapturedAuthLinkType(): AuthLinkType {
  return capturedAuthLinkType;
}

/** Test-only: resets the module-level capture so each test starts fresh. */
export function __resetCaptureForTests(): void {
  captured = false;
  capturedAuthLinkType = null;
}
